import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PhotoUploadModal = ({ 
  employee, 
  isOpen, 
  onClose, 
  onSave 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 200, height: 200 });
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Dateigröße muss weniger als 5MB betragen');
        return;
      }

      if (!file?.type?.startsWith('image/')) {
        alert('Bitte wählen Sie eine Bilddatei aus');
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e?.target?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleDrop = (event) => {
    event?.preventDefault();
    const file = event?.dataTransfer?.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleDragOver = (event) => {
    event?.preventDefault();
  };

  const handleSave = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real app, you would upload to a server here
      const photoUrl = previewUrl; // Use the preview URL as the final URL
      
      await onSave(employee?.id, photoUrl);
      onClose();
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error) {
      console.error('Photo upload failed:', error);
      alert('Foto-Upload fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setIsLoading(true);
    try {
      await onSave(employee?.id, null);
      onClose();
    } catch (error) {
      console.error('Photo removal failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden elevation-3">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Camera" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Mitarbeiterfoto aktualisieren
              </h2>
              <p className="text-sm text-text-secondary">
                {employee?.name} - {employee?.employeeCode}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="touch-feedback"
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Photo */}
          <div className="text-center">
            <h3 className="text-lg font-medium text-text-primary mb-4">Aktuelles Foto</h3>
            <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-muted">
              {employee?.photo ? (
                <Image
                  src={employee?.photo}
                  alt={employee?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="User" size={48} className="text-text-secondary" />
                </div>
              )}
            </div>
            
            {employee?.photo && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemovePhoto}
                loading={isLoading}
                iconName="Trash2"
                iconPosition="left"
                className="mt-4 touch-feedback text-error border-error/20 hover:bg-error hover:text-white"
              >
                Aktuelles Foto entfernen
              </Button>
            )}
          </div>

          {/* Upload Area */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Neues Foto hochladen</h3>
            
            {!previewUrl ? (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef?.current?.click()}
              >
                <Icon name="Upload" size={48} className="text-text-secondary mx-auto mb-4" />
                <div className="space-y-2">
                  <p className="text-lg font-medium text-text-primary">
                    Ziehen Sie Ihr Foto hierher oder klicken Sie zum Durchsuchen
                  </p>
                  <p className="text-sm text-text-secondary">
                    Unterstützt: JPG, PNG, GIF (Max 5MB)
                  </p>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Preview */}
                <div className="text-center">
                  <div className="w-48 h-48 mx-auto rounded-lg overflow-hidden bg-muted border border-border">
                    <Image
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* File Info */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Icon name="Image" size={20} className="text-primary" />
                      <div>
                        <div className="font-medium text-text-primary">
                          {selectedFile?.name}
                        </div>
                        <div className="text-sm text-text-secondary">
                          {selectedFile && (selectedFile?.size / 1024 / 1024)?.toFixed(2)} MB
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetUpload}
                      iconName="X"
                      className="touch-feedback"
                    />
                  </div>
                </div>

                {/* Upload Guidelines */}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} className="text-primary mt-0.5" />
                    <div className="space-y-2">
                      <h4 className="font-medium text-text-primary">Foto-Richtlinien</h4>
                      <ul className="text-sm text-text-secondary space-y-1">
                        <li>• Verwenden Sie ein klares, professionelles Porträtfoto</li>
                        <li>• Das Gesicht sollte deutlich sichtbar und gut beleuchtet sein</li>
                        <li>• Vermeiden Sie Sonnenbrillen oder Hüte, die das Gesicht verdecken</li>
                        <li>• Quadratisches Seitenverhältnis funktioniert am besten</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Abbrechen
          </Button>
          
          {previewUrl && (
            <Button
              variant="default"
              onClick={handleSave}
              loading={isLoading}
              iconName="Save"
              iconPosition="left"
              className="touch-feedback"
            >
              {isLoading ? 'Wird hochgeladen...' : 'Foto speichern'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadModal;